/**
 * FirstAddStep
 *
 * The "add a step at the start of the cadence" affordance, using the same
 * `CampaignNewDayPickerPure` chrome as the trailing new-day trigger. Extracted
 * from `CadenceSectionPure` so the prop wiring isn't repeated for the
 * empty-vs-populated branches, and rendered as a real component (not a
 * render-helper) at its call site.
 *
 * `afterStepId` is deliberately `undefined` so the new step lands BEFORE any
 * existing first step.
 */
import type { ReactNode } from 'react';

import { createStepDataFromEditor, daysToMs } from '../CGStep';
import type {
  EditStepPure,
  CreateStepData,
  CadenceStepTypePure,
  SiftConfig,
} from '../CGStep';
import { CampaignNewDayPickerPure } from '../CampaignDayCard/CampaignNewDayPickerPure';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { StepData, StepType } from '../CGStep/CTSContentEditingPure';
import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';

export type FirstAddStepProps = {
  editingStep: EditStepPure | undefined;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables: TipTapVariable[] | undefined;
  documents: TipTapDocument[] | undefined;
  /**
   * Subject the first step's email would continue — the cadence owner's latest email with the
   * contact, if any (see `findCadenceThreadSubjectForNewStep`). Null hides the Thread toggle.
   */
  previousEmailSubject: string | null;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  alreadyConnectedToContact?: boolean;
  voiceCallingEnabled?: boolean;
  onCreateStep: (data: CreateStepData) => Promise<void>;
};

export function FirstAddStep({
  editingStep,
  setEditingStep,
  variables,
  documents,
  previousEmailSubject,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  alreadyConnectedToContact,
  voiceCallingEnabled,
  onCreateStep,
}: FirstAddStepProps) {
  const FIRST_DELAY_MS = daysToMs(2);
  const isHere =
    editingStep?.type === 'add' &&
    editingStep.intent === 'new-day' &&
    editingStep.afterStepId === undefined;
  const isPickingHere = isHere && editingStep.data == null;
  const addingHere =
    isHere && editingStep.data != null
      ? (editingStep.data.type as CadenceStepTypePure)
      : undefined;

  return (
    <div className="mx-2 flex flex-col items-stretch">
      <CampaignNewDayPickerPure
        defaultDelayDays={2}
        previousEmailSubject={previousEmailSubject}
        // First-add picker — nothing above (start of the cadence).
        // The host adds its own spine BELOW (cadence-first-add-spine)
        // so we suppress the picker's bottom spine too to avoid
        // doubling up.
        hasNeighborAbove={false}
        hasNeighborBelow={false}
        isPickingStepType={isPickingHere}
        addingStepType={addingHere}
        existingTypes={[]}
        disabledStepReasons={
          alreadyConnectedToContact
            ? { connect: 'You are already connected to this contact.' }
            : undefined
        }
        variables={variables}
        documents={documents}
        renderSiftConditionBuilder={renderSiftConditionBuilder}
        reminderActions={reminderActions}
        reminderActionsLoading={reminderActionsLoading}
        onCreateReminder={onCreateReminder}
        reminderCreating={reminderCreating}
        onReminderError={onReminderError}
        voiceCallingEnabled={voiceCallingEnabled}
        onAddStep={() =>
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
          })
        }
        onCancelPickStepType={() => setEditingStep(undefined)}
        onPickStepType={(type) => {
          if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
            void onCreateStep({
              type: type as StepType,
              delay: FIRST_DELAY_MS,
              afterStepId: undefined,
              ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
            });
            setEditingStep(undefined);
            return;
          }
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
            data: { id: 'pending', type: type as StepType, delay: FIRST_DELAY_MS },
          });
        }}
        onSaveNewStep={async (type, data: StepData) => {
          const create = createStepDataFromEditor(type as StepType, data, {
            delay: FIRST_DELAY_MS,
            afterStepId: undefined,
          });
          await onCreateStep(create);
          setEditingStep(undefined);
        }}
        onCancelAddStep={() => {
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
          });
        }}
      />
      {/* Centered vertical spine linking this trigger / picker down to
          the first day card, matching the spine that threads through
          inter-day wait bands. The negative top margin closes the gap
          left by the trigger button's `my-3` so the line starts flush
          against the bottom of the button. */}
      <span
        aria-hidden
        data-testid="cadence-first-add-spine"
        className="mx-auto -mt-3 block h-3 w-px bg-lynch-200 dark:bg-slate-700"
      />
    </div>
  );
}
